import LoadingIcon from '../../generic/loading-icon';
import Mustache from 'mustache';
import carseachDataConverter from '../../bavaria/carsearch/carseach-data-converter';




/**
 * TestDrive class holds a simple car search that return
 * suitable cars choices that are test drivable in the location.
 */
class TestDrive {

  /**
   * Defines scope of the TestDrive class instance
   */
  #element;

  /**
   * @property {object} dom - HTMLElements references
   */
  #dom;

  /**
   * @property {object} data - Data loaded from the JSON embedded to HTML. Data like Location's preresented brands..
   */
  #data = {};

  /**
   * @property {object} noResultsTexts - Object for strings that are shown when user have to select next option or results not found
   */
  #noResultsText = {
    selectLocation: 'Valitse toimipiste',
    selectBrand: 'Valitse merkki',
    selectModel: 'Valitse malli',
    noResults: 'Ei hakutuloksia'
  }



  /**
   * @constructor
   * @param {HTMLElement} element - logic is bind to this element
   */
  constructor( element ) {
    this.#element = element;
    this.#findElements();
    this.#bindEventListeners();
    this.#loadDataAndTemplates();
    this.#asyncronousInit();
  }

  /**
   * Set element state
   * @param {boolean} isLoading pass true to show loading spinner or false to hide it
   */
  set loading( isLoading ) {
    LoadingIcon.instances['test-drive-loader'].show = isLoading;
    this.#dom.loadContainer.classList.toggle('show', isLoading);
  }

  get site() {
    return ['bmw', 'mini'].includes(document.body.dataset.brand) ? 'bavaria' : 'laakkonen';
  }

  /**
   * Initializes all TestDrive components on the page.
   */
  static init() {
    document.querySelectorAll('.test-drive-form').forEach((element) => new TestDrive(element));
  }

  /**
   * Read parameters from URL-address
   * @return {object} parameters
   */
  get parameters() {
    const parametersObj = {};

    //Read parameters from URL address
    for ( const[key, value] of new URLSearchParams( window.location.search ) ) {
      parametersObj[key] = value;
    }

    return parametersObj;
  }

  /**
   * Set brand & model multiselect state from GET-parameters.
   * Makes car search based on selection and renders results to page.
   */
  async #asyncronousInit() {
    //Set brand options and models
    const [dealer_id] = this.#dom.locations.getValue();
    this.#updateBrandOptions(dealer_id);
    const [brandSlug] = this.#dom.brands.getValue();
    await this.#updateModelOptions(dealer_id, brandSlug);
    const [modelSlug] = this.#dom.models.getValue();

    if( dealer_id && brandSlug && modelSlug ) {
      this.loading = true;
      const response = await this.#fetchCars()
      this.#renderCars( response );
      this.loading = false;
    }

    this.#updateBrandAndModelMultiselectedState();
  }

  /**
   * Find elements that are controlled by TestDrive instance for later access.
   */
  #findElements() {
    this.#dom = {
      preresentedBrands: this.#element.querySelector('script.location-preresented-brands'),
      templates: this.#element.querySelector('script.templates'),
      locations: window.multiselects['filter-locations'],
      brands: window.multiselects['filter-brands'],
      models: window.multiselects['filter-models'],
      loadContainer: this.#element.querySelector('.loader-container'),
      grid: this.#element.querySelector('.cars'),
      noSearchResult: this.#element.querySelector('.test-drive-form-no-search-result')
    }
  }

  /**
   * Bind event listeners
   */
  #bindEventListeners() {
    this.#dom.locations?.addCallback({ onValueChange: this.#onLocationChanges.bind(this) });
    this.#dom.brands?.addCallback({ onValueChange: this.#onBrandChanges.bind(this) });
    this.#dom.models?.addCallback({ onValueChange: this.#onModelChanges.bind(this) });
  }

  /**
   * Set brand and model enabled or disabled
   */
  #updateBrandAndModelMultiselectedState() {
    this.#dom.brands.toggleMultiselect( this.#dom.brands.options.length > 1 );
    this.#dom.models.toggleMultiselect( this.#dom.models.options.length > 1 );
  }

  /**
   * Update brand options
   * @param {string} dealer_id Dealer's identification string like 'HA6'
   */
  #updateBrandOptions(dealer_id) {

    //Location's preresented brands
    const loadPreresentedBrand = this.#data.loadPreresentedBrands.find( brand => dealer_id && brand.dealer_id === dealer_id );

    let newOptions = [loadPreresentedBrand?.carbrands_service_data].flat().filter(Boolean).map( data => {
      return {
        value: data.value,
        shownValue: data.text,
        selected: this.parameters?.brands === data.value
      }
    });

    this.#dom.brands.removeAllOptions(false);
    this.#dom.brands.addOptions(newOptions);
  }

  /**
   * Brand's slug like 'bmw', 'skoda', or something similar
   * @param {string} dealerId - Dealer id like 'HA4'
   * @param {string} brandSlug - Brand's slug
   */
  async #updateModelOptions(dealerId, brandSlug) {

    this.#dom.models.removeAllOptions(false);

    if( dealerId && brandSlug ) {
      const models = await this.#fetchModels(dealerId, brandSlug);

      const options = models.map( option => ({
        value: option.model_slug,
        shownValue: option.model_name,
        selected: option.model_slug === this.parameters?.models
      }));

      this.#dom.models.addOptions(options);
    }
  }

  /**
   * Called when a user changes a location in location multiselect
   */
  #onLocationChanges() {

    this.#emptyVehiclesArea();

    const [ dealer_id ] = this.#dom.locations.getValue();

    this.#dom.brands.removeAllOptions(false);
    this.#dom.models.removeAllOptions(false);
    this.#dom.noSearchResult.innerText = this.#getNoResultsText();

    this.#updatehHistory();

    this.#updateBrandOptions( dealer_id );

    this.#updateBrandAndModelMultiselectedState();
  }

  /**
   * Called when a user changes a brand in brand multiselect
   */
  async #onBrandChanges() {
    this.#emptyVehiclesArea();
    this.#dom.noSearchResult.classList.toggle('show', true);
    const [ brandSlug ] = this.#dom.brands.getValue();
    const [ dealerId ] = this.#dom.locations.getValue();
    this.#dom.noSearchResult.innerText = this.#getNoResultsText();
    await this.#updateModelOptions(dealerId, brandSlug);
    this.#updateBrandAndModelMultiselectedState();

    this.#updatehHistory();
  }

  /**
   * Called when a user changes a model in model multiselect
   */
  async #onModelChanges() {
    this.#updatehHistory();
    this.loading = true;
    this.#dom.noSearchResult.classList.toggle('show', false );
    this.#emptyVehiclesArea();

    const [dealer_id] = this.#dom.locations.getValue();
    const [brands] = this.#dom.brands.getValue();
    const [models] = this.#dom.models.getValue();

    if( dealer_id && brands && models ) {
      const response = await this.#fetchCars();
      this.#renderCars( response );
    } else {
      this.#dom.noSearchResult.innerText = this.#getNoResultsText();
      this.#dom.noSearchResult.classList.toggle('show', true );
    }

    this.loading = false;
  }

  /**
   * Render vehicles with Mustache to HTML
   * @param {object} data - vehicle search response object
   */
  #renderCars(data) {
    const html = data.vehicles.map( vehicle => {

      //Due we use Bavaria's car card we but Laakkonen carsearch we need to convert data to format that Bavaria car card template understand.
      let vehicleData = this.site === 'bavaria' ? carseachDataConverter(vehicle) : vehicle;

      //On Laakkonen page Mustatche is wrapped to global module that is exported from Stage calledwindow.Dali.
      //It just app some "helper function" or partials that are used rendering Laakkonen car card templates.
      const renderFunction = this.site === 'laakkonen' ? window.Dali.render : Mustache.render;

      return renderFunction( this.#data.templates.carCard.template, {
        ...vehicleData,
        cardCustomClasses: this.#data.templates.carCard.cardCustomClasses
      } , this.#data.templates.carCard.partials );

    }).join('');

    this.#dom.grid.innerHTML = html;

    this.#dom.noSearchResult.innerText = this.#getNoResultsText();
    this.#dom.noSearchResult.classList.toggle('show', data.vehicles.length === 0 );
  }

  #emptyVehiclesArea() {
    this.#dom.grid.innerHTML = '';
  }

  /**
   * Update history or URL-adress when location, brand or model is changed
   */
  #updatehHistory() {
    const urlSearchParams = this.#getUrlSearchParams();
    const url = `${window.location.origin}${window.location.pathname}?${urlSearchParams.toString()}`;
    window.history.replaceState({}, '', url);
  }

  /**
   * Return URL search parameters
   * @returns {URLSearchParams} url search params formed from selections
   */
  #getUrlSearchParams() {
    const urlSearchParams = new URLSearchParams();
    const [dealer_id] = this.#dom.locations.getValue();
    const [brands] = this.#dom.brands.getValue();
    const [models] = this.#dom.models.getValue();
    const urlParams = {dealer_id, brands, models};
    Object.keys( urlParams ).forEach( key => {
      if( urlParams[key] ) {
        urlSearchParams.append( key, urlParams[key] );
      }
    });
    return urlSearchParams;
  }

  /**
   * Return models by brand
   * @async
   * @param {string} dealer_id - Dealer id like 'HA4'
   * @param {string} brandSlug - Brand slug like 'bmw', 'skoda', 'laakkonen' or similar.
   * @returns {object} data
   */
  async #fetchModels(dealer_id, brandSlug) {
    const response = await fetch(`/stage-ajax/test-drive-2022-get-models/?brand=${brandSlug}&dealer_id=${dealer_id}`)
    return response.json();
  }

  /**
   * Execute car search
   * @async
   * @returns {object} car search result
   */
  async #fetchCars() {
    const urlSearchParams = this.#getUrlSearchParams();
    urlSearchParams.append('carsSearchType', 'td');
    urlSearchParams.append('category', 'esittelyautot');
    urlSearchParams.append('noCache', 'true');
    const response = await fetch(`/stage-ajax/2022-car-search?${urlSearchParams.toString()}`);
    const responseJson = await response.json();

    responseJson.vehicles.forEach( vehicle => {
      vehicle.listlink = this.#element.dataset.link.replace('--license--', vehicle.license );
    });

    return responseJson;
  }

  /**
   * Load data and templates from DOM
   */
  #loadDataAndTemplates() {
    try {
      this.#data.loadPreresentedBrands = JSON.parse( this.#dom.preresentedBrands.innerText )
      this.#data.templates = JSON.parse(this.#dom.templates.innerText);
    } catch( error ) {
      console.error('Error loading presented brands JSON-data', error );
    }
  }

  /**
   * Returns no results container text depending on what selections are made
   *
   * @returns {string}
   */
  #getNoResultsText() {
    if(!this.#dom.locations.getValue().length) {
      return this.#noResultsText.selectLocation;
    } else if(!this.#dom.brands.getValue().length) {
      return this.#noResultsText.selectBrand;
    } else if(!this.#dom.models.getValue().length) {
      return this.#noResultsText.selectModel
    } else {
      return this.#noResultsText.noResults;
    }
  }
}

export default TestDrive;
